/**
 * Compressão no aparelho (AR §8.4): a API nunca processa imagem, e a VPS de
 * 2 vCPU agradece. Três variantes por foto:
 *
 *   otimizada  lado maior ≤ 3000 px · qualidade 0,8 · é o "original" guardado
 *   prévia     ≤ 1280 px · JPEG 0,8 · o que a tela de foto mostra
 *   miniatura  ≤ 320 px · JPEG 0,75 · a grade do álbum
 *
 * Perfis iniciais do AR, a medir em aparelho real antes de congelar.
 * PNG continua PNG na otimizada (transparência); prévia e miniatura são
 * sempre JPEG. HEIC do iPhone: o Safari entrega JPEG ao `<input>` quando
 * `accept` é `image/*`; se ainda assim vier algo que o navegador não
 * decodifica, `variantes` falha e a fila mostra o erro — nada é enviado às
 * cegas.
 */

export interface Plano {
  lado: number;
  tipo: 'image/jpeg' | 'image/png' | 'image/webp';
  qualidade: number;
}

export const PERFIS = {
  otimizada: { lado: 3000, qualidade: 0.8 },
  preview: { lado: 1280, qualidade: 0.8 },
  thumb: { lado: 320, qualidade: 0.75 },
} as const;

/** Dimensões de saída mantendo a proporção; nunca amplia. */
export function dimensoesPara(largura: number, altura: number, ladoMaximo: number): { w: number; h: number } {
  const maior = Math.max(largura, altura);
  if (maior <= ladoMaximo) return { w: largura, h: altura };
  const fator = ladoMaximo / maior;
  return { w: Math.max(1, Math.round(largura * fator)), h: Math.max(1, Math.round(altura * fator)) };
}

/** Tipo da variante otimizada: PNG preserva transparência; o resto vira JPEG. */
export function tipoDaOtimizada(tipoOriginal: string): Plano['tipo'] {
  if (tipoOriginal === 'image/png') return 'image/png';
  if (tipoOriginal === 'image/webp') return 'image/webp';
  return 'image/jpeg';
}

export interface Variantes {
  otimizada: Blob;
  preview: Blob;
  thumb: Blob;
  width: number;
  height: number;
  tipo: Plano['tipo'];
}

async function decodificar(arquivo: Blob): Promise<ImageBitmap> {
  // `from-image` aplica a orientação EXIF: a foto deitada não sobe deitada.
  return createImageBitmap(arquivo, { imageOrientation: 'from-image' } as ImageBitmapOptions);
}

async function desenhar(bitmap: ImageBitmap, lado: number, tipo: string, qualidade: number): Promise<Blob> {
  const { w, h } = dimensoesPara(bitmap.width, bitmap.height, lado);
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Sem canvas 2D neste navegador.');
  ctx.drawImage(bitmap, 0, 0, w, h);
  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('Não foi possível codificar a imagem.'))), tipo, qualidade);
  });
}

export async function variantes(arquivo: File): Promise<Variantes> {
  const bitmap = await decodificar(arquivo);
  try {
    const tipo = tipoDaOtimizada(arquivo.type);
    const [otimizada, preview, thumb] = await Promise.all([
      desenhar(bitmap, PERFIS.otimizada.lado, tipo, PERFIS.otimizada.qualidade),
      desenhar(bitmap, PERFIS.preview.lado, 'image/jpeg', PERFIS.preview.qualidade),
      desenhar(bitmap, PERFIS.thumb.lado, 'image/jpeg', PERFIS.thumb.qualidade),
    ]);
    const dims = dimensoesPara(bitmap.width, bitmap.height, PERFIS.otimizada.lado);
    return { otimizada, preview, thumb, width: dims.w, height: dims.h, tipo };
  } finally {
    bitmap.close();
  }
}

/**
 * Uma variante só, em JPEG — para fotos utilitárias que não são memória
 * (a placa da fileira do estacionamento): cabe no envio e some depois.
 */
export async function fotoReduzida(arquivo: File, lado = 1280, qualidade = 0.8): Promise<Blob> {
  const bitmap = await decodificar(arquivo);
  try {
    return await desenhar(bitmap, lado, 'image/jpeg', qualidade);
  } finally {
    bitmap.close();
  }
}
